import React from "react";
import axios from "axios";

import { apiClient } from "../../api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import {
  type RuleQueryResult,
  type Rule,
  RuleTableUpdateArg,
} from "../../types/rule";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

export function useCreateRule({ table }: RuleTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rule: Rule) => {
      try {
        const response = await apiClient.post("/api/rules/", rule);

        table.setCreatingRow(null);
        toast.success(`Rule ${rule.title} created successfully`);
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error creating rule: ${error}`);
        }
      }
    },
    onSuccess: (newRule: Rule) => {
      queryClient.setQueryData<Rule[]>(["rule"], (preRules = []) => [
        ...preRules,
        newRule,
      ]);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["rule"] }),
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      try {
        await apiClient.delete(`/api/rules/${ruleId}`);
        toast.success("Rule successfully deleted");
      } catch (error) {
        toast.error("Failed to delete the rule");
      }
    },
    onMutate: (ruleId: string) => {
      queryClient.setQueryData(["rule"], (prevRules: any) =>
        prevRules?.filter((rule: Rule) => rule.id !== ruleId)
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["rule"] }),
  });
}

export const useExportRules = (columnFilters: any, sorting: any) => {
  const exportRules = async () => {
    try {
      const response = await apiClient.post("/api/rules/export/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;

      const now = new Date();
      const dateString = now.toISOString().split("T")[0];
      const timestamp = now.getTime();
      const filename = `Rules-${dateString}-${timestamp}.csv`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();

      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download file : ${error}`);
    }
  };

  return exportRules;
};

export function useSearchFindings({ q }: { q: string }) {
  return useQuery<RuleQueryResult[]>({
    queryKey: ["rule", q],
    queryFn: async () => {
      try {
        const url = `/api/rules?search=${q}`;

        const response = await apiClient.get(url);

        return [
          {
            rules: response.data.results as Rule[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ rules: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetAllRules() {
  return useQuery<RuleQueryResult[]>({
    queryKey: ["all_rules"],
    queryFn: async () => {
      try {
        const url = `/api/rules`;

        const response = await apiClient.get(url);
        return [
          {
            rules: response.data.results as Rule[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ rules: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetRules({
  pagination,
  columnFilters,
  sorting,
  globalFilter,
  exportCsv,
}: {
  pagination: MRT_PaginationState;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
  globalFilter: string | undefined;
  exportCsv?: boolean;
}) {
  return useQuery<RuleQueryResult[]>({
    queryKey: [
      "rules",
      pagination,
      columnFilters,
      sorting,
      globalFilter,
      exportCsv,
    ],
    queryFn: async () => {
      try {
        const url = `/api/rules`;
        const params = new URLSearchParams({
          page: (pagination.pageIndex + 1).toString(),
          limit: pagination.pageSize.toString(),
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
          globalFilter: globalFilter ?? "",
        });

        const response = await apiClient.get(url, { params });

        return [
          {
            rules: response.data.results as Rule[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ rules: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetRuleDetail({ title }: { title: string }) {
  return useQuery<Rule[]>({
    queryKey: ["rule"],
    queryFn: async () => {
      try {
        const url = `/api/rules`;
        const params = new URLSearchParams({
          title: title,
        });

        const response = await apiClient.get(url, { params });

        return response.data.results;
      } catch (error) {
        //
        return {};
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useUpdateRule({ table }: RuleTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rule: Rule) => {
      try {
        await apiClient.patch(`/api/rules/${rule.id}/`, rule);

        table.setEditingRow(null);
        toast.success(`Rule ${rule.title} updated successfully`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error updating riskmap ${error}`);
        }
      }
    },

    onMutate: (newRuleInfo: Rule) => {
      queryClient.setQueryData(["rule"], (prevRules: any) =>
        prevRules?.map((prevRule: Rule) =>
          prevRule.id === newRuleInfo.id ? newRuleInfo : prevRule
        )
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["rule"] }),
  });
}

export const useDeleteRules = (
  columnFilters: any,
  sorting: any,
  rowCount: number
) => {
  const deleteRules = async () => {
    try {
      await apiClient.post("/api/rules/delete/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
      });

      toast.success(`Deleted ${rowCount} rows successfully`);
    } catch (error) {
      toast.error("Failed to delete rows");
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  return deleteRules;
};

export const useApplyRules = () => {
  const applyRules = async () => {
    try {
      await apiClient.get("/api/rules/applyall/", {
        params: {},
      });

      toast.success(`Applied successfully`);
    } catch (error) {
      toast.error("Failed to apply");
    } finally {
      /* setTimeout(() => {
                 window.location.reload();
             }, 1000); */
    }
  };

  return applyRules();
};
